if (!customElements.get('zereniyo-product')) {
  class ZereniyoProduct extends HTMLElement {
    connectedCallback() {
      this.variantData = JSON.parse(this.querySelector('[data-z-product-variants]').textContent);
      this.optionGroups = Array.from(this.querySelectorAll('[data-z-option-group]'));
      this.variantInput = this.querySelector('[data-z-variant-id]');
      this.price = this.querySelector('[data-z-price]');
      this.comparePrice = this.querySelector('[data-z-compare-price]');
      this.variantLabel = this.querySelector('[data-z-variant-label]');
      this.stock = this.querySelector('[data-z-stock]');
      this.sku = this.querySelector('[data-z-sku]');
      this.addButton = this.querySelector('[data-z-add]');
      this.addButtonText = this.querySelector('[data-z-add-text]');
      this.quantity = this.querySelector('[data-z-quantity]');
      this.lineTotal = this.querySelector('[data-z-line-total]');
      this.totalPrice = this.querySelector('[data-z-total-price]');
      this.bulkNote = this.querySelector('[data-z-bulk-note]');
      this.media = Array.from(this.querySelectorAll('[data-z-media]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-z-thumb]'));

      this.onOptionChange = this.onOptionChange.bind(this);
      this.onQuantityChange = this.onQuantityChange.bind(this);
      this.optionGroups.forEach((group) => group.addEventListener('change', this.onOptionChange));
      this.quantity?.addEventListener('change', this.onQuantityChange);
      this.quantity?.addEventListener('input', this.onQuantityChange);
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.showMedia(thumb.dataset.zThumb)));

      this.currentVariant = this.variantData.find((item) => String(item.id) === this.variantInput?.value) || this.variantData[0];
      this.updatePurchaseDetails();
    }

    onOptionChange() {
      const options = this.optionGroups.map((group) => group.querySelector('input:checked')?.value);
      const variant = this.variantData.find((item) => item.options.every((value, index) => value === options[index]));
      this.updateOptionAvailability(options);
      this.updateVariant(variant);
    }

    updateOptionAvailability(selectedOptions) {
      this.optionGroups.forEach((group, groupIndex) => {
        group.querySelectorAll('input').forEach((input) => {
          const possible = this.variantData.some((variant) => {
            if (!variant.available || variant.options[groupIndex] !== input.value) return false;
            return variant.options.every((value, index) => index === groupIndex || value === selectedOptions[index]);
          });
          input.classList.toggle('is-unavailable', !possible);
          input.nextElementSibling?.classList.toggle('is-unavailable', !possible);
        });
      });
    }

    updateVariant(variant) {
      this.currentVariant = variant || null;
      if (!variant) {
        this.variantInput.value = '';
        this.addButton.disabled = true;
        this.addButtonText.textContent = this.dataset.unavailableLabel;
        this.updatePurchaseDetails();
        return;
      }

      this.variantInput.value = variant.id;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.price.textContent = variant.priceFormatted;
      this.comparePrice.textContent = variant.compareAtPriceFormatted || '';
      this.comparePrice.hidden = !variant.compareAtPriceFormatted;
      this.variantLabel.textContent = variant.title === 'Default Title' ? '' : variant.title;
      this.variantLabel.hidden = variant.title === 'Default Title';
      this.sku.textContent = variant.sku || this.dataset.noSkuLabel;

      const lowStock = variant.inventoryManagement === 'shopify' && variant.inventoryQuantity > 0 && variant.inventoryQuantity <= Number(this.dataset.lowStockThreshold);
      this.stock.classList.toggle('is-low', lowStock);
      this.stock.classList.toggle('is-sold-out', !variant.available);
      if (!variant.available) this.stock.textContent = this.dataset.soldOutLabel;
      else if (lowStock) this.stock.textContent = `${this.dataset.lowStockLabel} · ${variant.inventoryQuantity} ${this.dataset.leftLabel}`;
      else this.stock.textContent = this.dataset.inStockLabel;

      this.addButton.disabled = !variant.available;
      this.addButtonText.textContent = variant.available ? this.dataset.addLabel : this.dataset.soldOutLabel;
      this.quantity.min = variant.quantityMin || 1;
      this.quantity.step = variant.quantityIncrement || 1;
      this.quantity.value = variant.quantityMin || 1;
      if (variant.quantityMax) this.quantity.max = variant.quantityMax;
      else this.quantity.removeAttribute('max');
      this.updatePurchaseDetails();

      if (variant.featuredMediaId) this.showMedia(String(variant.featuredMediaId));
      if (this.dataset.updateUrl !== 'false') window.history.replaceState({}, '', `${this.dataset.productUrl}?variant=${variant.id}`);
    }

    onQuantityChange() {
      this.updateLineTotal();
    }

    updatePurchaseDetails() {
      this.updateLineTotal();
      if (this.bulkNote) this.bulkNote.hidden = !this.isBulkVariant(this.currentVariant);
    }

    updateLineTotal() {
      if (!this.lineTotal) return;
      const quantity = Number.parseInt(this.quantity?.value, 10);
      const showTotal = Boolean(this.currentVariant && Number.isFinite(quantity) && quantity > 1);
      this.lineTotal.hidden = !showTotal;
      if (!showTotal) return;

      this.totalPrice.textContent = this.formatMoney(this.currentVariant.price * quantity);
    }

    isBulkVariant(variant) {
      if (!variant) return false;

      return variant.options.some((option) => {
        const matches = String(option).toLowerCase().matchAll(/(\d+(?:[.,]\d+)?)\s*(kg|kgs|kilograms?|g|grams?)\b/g);
        for (const match of matches) {
          const amount = Number.parseFloat(match[1].replace(',', '.'));
          const grams = match[2].startsWith('k') ? amount * 1000 : amount;
          if (grams >= 1000) return true;
        }
        return false;
      });
    }

    formatMoney(cents) {
      const template = this.dataset.moneyFormat || 'Rs. {{amount}}';
      const placeholder = template.match(/{{\s*(\w+)\s*}}/);
      if (!placeholder) return String((cents / 100).toFixed(2));

      const formats = {
        amount: [2, ',', '.'],
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
        amount_no_decimals_with_space_separator: [0, ' ', '.'],
        amount_with_space_separator: [2, ' ', ','],
      };
      const [precision, thousands, decimal] = formats[placeholder[1]] || formats.amount;
      const number = (cents / 100).toFixed(precision);
      const parts = number.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const formatted = precision ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
      return template.replace(placeholder[0], formatted);
    }

    showMedia(mediaId) {
      const target = this.media.find((item) => item.dataset.zMedia === mediaId);
      if (!target) return;
      this.media.forEach((item) => item.classList.toggle('is-active', item === target));
      this.media.forEach((item) => { item.hidden = item !== target; });
      this.thumbs.forEach((item) => {
        const active = item.dataset.zThumb === mediaId;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-current', String(active));
      });
    }
  }

  customElements.define('zereniyo-product', ZereniyoProduct);
}
